import Stomp from 'stompjs';
import SockJS from 'sockjs-client/dist/sockjs.min.js';
import { tryOnUnmounted } from '@vueuse/core';
import { App, getCurrentInstance } from 'vue';
import mitt from '/@/utils/mitt';

// 定义事件类型常量（避免字符串硬编码）
export const WS_EVENTS = {
  RECONNECT: 'ws:reconnect',
  ERROR: 'ws:error',
  MAX_RETRY_REACHED: 'ws:maxRetry',
};

export declare interface StompClient {
  subscribe(
    topic: string,
    callback: (msg: any) => void,
    forceUnsubscribe?: boolean,
    // 自定义 STOMP header（如英麒雷达的 yingqi-ticket）
    headers?: Record<string, string>,
  ): void;
  unsubscribe(topic: string): void;
  isConnected(): boolean;
}

export const emitter = mitt();

function createStomp(url: string[]): StompClient[] {
  const clients: any[] = url.map((currentUrl, index) => {
    // 内部状态管理
    const subscribes: Record<string, Stomp.Subscription> = {};
    const pendingSubscriptions: Array<{
      topic: string;
      callback: (msg: any) => void;
      headers?: Record<string, string>;
    }> = [];
    let stompClient: Stomp.Client | null = null;
    let isConnected = false;
    let retryCount = 0;
    const maxRetries = 30;
    const baseDelay = 1000;
    const maxDelay = 10000;
    let isMaxRetryReached = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const _cleanup = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const _initWebsocket = () => {
      // 如果已经达到最大重试次数，不再尝试连接
      if (isMaxRetryReached) {
        return;
      }

      try {
        const socket = new SockJS(currentUrl);
        stompClient = Stomp.over(socket);
        stompClient.debug = null; // 关闭调试日志

        // 心跳配置
        stompClient.heartbeat.outgoing = 5000;
        stompClient.heartbeat.incoming = 5000;

        const connectTimeout = setTimeout(() => {
          if (!isConnected) {
            console.warn('[WS] 连接超时');
            _closeWebsocket();
            _handleReconnect();
          }
        }, 10000);

        stompClient.connect(
          {},
          () => {
            clearTimeout(connectTimeout);
            console.log(`[WS] 连接成功: ${currentUrl}`);
            isConnected = true;
            retryCount = 0; // 重置重试计数器
            isMaxRetryReached = false;

            // 处理待订阅队列（强制取消旧订阅）。先取快照再遍历，避免回放过程中
            // unsubscribe 修改数组导致漏订
            pendingSubscriptions.splice(0).forEach((sub) => {
              subscribe(sub.topic, sub.callback, true, sub.headers);
            });

            // 安全触发事件（检查是否有监听器）
            if (emitter.all?.has(WS_EVENTS.RECONNECT)) {
              emitter.emit(WS_EVENTS.RECONNECT);
            }
          },
          (error) => {
            clearTimeout(connectTimeout);
            if (!isMaxRetryReached) {
              // 只在未达到最大重试时打印错误
              console.error('[WS] 连接错误:', error);
              emitter.emit(WS_EVENTS.ERROR, error);
            }
            isConnected = false;
            _handleReconnect();
          },
        );
      } catch (e) {
        if (!isMaxRetryReached) {
          console.error('[WS] 初始化异常:', e);
        }
        _handleReconnect();
      }
    };

    // 统一处理重连逻辑
    const _handleReconnect = () => {
      _cleanup(); // 清理之前的定时器

      retryCount++;
      if (retryCount <= maxRetries) {
        // 指数退避
        const delay = Math.min(baseDelay * Math.pow(2, retryCount - 1), maxDelay);
        console.log(`[WS] ${delay / 1000}秒后重试 (${retryCount}/${maxRetries})`);
        reconnectTimer = setTimeout(_initWebsocket, delay);
      } else if (!isMaxRetryReached) {
        isMaxRetryReached = true;
        console.error('[WS] 达到最大重试次数，停止重连');
        emitter.emit(WS_EVENTS.MAX_RETRY_REACHED);
      }
    };

    const _closeWebsocket = () => {
      if (!stompClient) return;

      _cleanup(); // 清理重连定时器

      try {
        stompClient.disconnect(() => {
          console.log('[WS] 连接已关闭');
        });
        Object.keys(subscribes).forEach(unsubscribe);
      } catch (e) {
        if (!isMaxRetryReached) {
          console.error('[WS] 关闭连接异常:', e);
        }
      } finally {
        isConnected = false;
        stompClient = null;
      }
    };

    // 公开方法
    const subscribe = (
      topic: string,
      callback: (msg: any) => void,
      forceUnsubscribe = true,
      headers?: Record<string, string>,
    ) => {
      // 强制取消旧订阅（防止重复）
      if (forceUnsubscribe) unsubscribe(topic);

      if (!stompClient || !isConnected) {
        // 存入待订阅队列（自动去重）
        if (!pendingSubscriptions.some((sub) => sub.topic === topic)) {
          pendingSubscriptions.push({ topic, callback, headers });
        }
        return;
      }

      try {
        if (subscribes[topic]) unsubscribe(topic);

        const subInfo = stompClient.subscribe(
          topic,
          (res) => {
            try {
              callback(JSON.parse(res.body));
            } catch (e) {
              console.error(`[WS] 消息解析错误 (${topic}):`, e);
            }
          },
          // stompjs 会往传入的 headers 里补 id/destination，必须拷贝，避免污染调用方对象
          headers ? { ...headers } : undefined,
        );

        subscribes[topic] = subInfo;
        console.log(`[WS] 订阅成功: ${topic}`);
      } catch (e) {
        console.error(`[WS] 订阅失败 (${topic}):`, e);
        pendingSubscriptions.push({ topic, callback, headers });
      }
    };

    const unsubscribe = (topic: string) => {
      // 取消活跃订阅
      if (subscribes[topic]) {
        try {
          subscribes[topic].unsubscribe();
        } catch (e) {
          // 重连后旧连接上的订阅已失效，此时发送 UNSUBSCRIBE 可能抛错，忽略即可
          console.error(`[WS] 取消订阅异常 (${topic}):`, e);
        }
        delete subscribes[topic];
        console.log(`[WS] 取消订阅: ${topic}`);
      }

      // 从待订阅队列移除
      const index = pendingSubscriptions.findIndex((sub) => sub.topic === topic);
      if (index !== -1) {
        pendingSubscriptions.splice(index, 1);
      }
    };

    const checkIsConnected = () => isConnected;

    // Vue插件安装
    const install = (app: App) => {
      const propKey = `$stompClient${index + 1}`;
      app.config.globalProperties[propKey] = {
        subscribe,
        unsubscribe,
        isConnected: checkIsConnected,
      } as StompClient;
    };

    _initWebsocket();
    tryOnUnmounted(_closeWebsocket);

    return { install };
  });
  return clients;
}

// 初始化方法
export function setupStomp(app: App, urls: string[]): void {
  const stompClients = createStomp(urls);
  stompClients.forEach((client: any) => app.use(client));
}

// 组合式函数
export function useStompClient(index = 1): StompClient {
  const app = getCurrentInstance();
  const propKey = `$stompClient${index}`;
  return app?.appContext.config.globalProperties[propKey];
}
